/**
 * 日期＋時間輸入（2026-08-29）
 *
 * 取代原本的單一 `<input type="datetime-local">`：Chrome 的日期選擇器會在使用者挑日期時，
 * 把時間自動填成「開啟表單當下的時刻」，使用者不會察覺 —— 訂單 ORD260820ZU823K 的上車時間
 * 被填成 08:55（開表單的時刻）而非航班抵達的 20:05，就是這樣造成的。
 *
 * 拆成兩個欄位後，時間沒填就是空的，驗證擋得住，也不會有被瀏覽器猜錯的值。
 */
import { useLanguage } from "../../contexts/LanguageContext";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import type { Loc } from "../../../../shared/const";
import { joinLocalDateTime, splitLocalDateTime, todayLocalDate } from "../../../../shared/local-datetime";

export function DateTimeField({
  label,
  hint,
  value,
  onChange,
  error,
  idPrefix = "dt",
}: {
  label: Loc;
  /** 欄位下方的說明（例如提醒填航班抵達時間而非期望上車時間） */
  hint?: Loc;
  /** "YYYY-MM-DDTHH:mm"；未填完整時為空字串 */
  value: string;
  onChange: (next: string) => void;
  /** 已顯示的錯誤訊息（由呼叫端決定何時顯示） */
  error?: string | null;
  idPrefix?: string;
}) {
  const { t } = useLanguage();
  const { date, time } = splitLocalDateTime(value);

  return (
    <div className="space-y-1.5">
      <Label className="text-[12px]">{t(label)} *</Label>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Input
            id={`${idPrefix}-date`}
            type="date"
            min={todayLocalDate(Date.now())}
            value={date}
            onChange={(e) => onChange(joinLocalDateTime(e.target.value, time))}
            aria-label={t({ "zh-TW": "日期", "en-US": "Date", "zh-CN": "日期" })}
          />
          <p className="px-0.5 text-[12px] text-muted-foreground">
            {t({ "zh-TW": "日期", "en-US": "Date", "zh-CN": "日期" })}
          </p>
        </div>
        <div className="space-y-1">
          <Input
            id={`${idPrefix}-time`}
            type="time"
            value={time}
            onChange={(e) => onChange(joinLocalDateTime(date, e.target.value))}
            aria-label={t({ "zh-TW": "時間", "en-US": "Time", "zh-CN": "时间" })}
          />
          <p className="px-0.5 text-[12px] text-muted-foreground">
            {t({ "zh-TW": "時間（24 小時制）", "en-US": "Time (24h)", "zh-CN": "时间（24 小时制）" })}
          </p>
        </div>
      </div>
      {hint && <p className="text-[12px] leading-relaxed text-muted-foreground">{t(hint)}</p>}
      {error && <p className="text-[12px] text-destructive">{error}</p>}
    </div>
  );
}
